import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnInit,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmeraldModalModule } from '@emerald-angular/modal';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { PersonaType, SkillType } from '@app/shared/constants/global.enum';
import {
  Contractors,
  ContractorsBySkills,
  TimeSheetData,
  TimesheetResponse,
} from '@app/shared/interfaces/vendor-management-model';
import { ToasterService } from '@app/shared/services/toaster.service';
import { DataService } from '@app/shared/services/data.service';

import {
  getProjectOptions,
  getVendorOptions,
  sortDropdownValues,
} from './vendor-management-filter.helper';
import { dropdownValues } from '@interfaces/common.model';
import { filterData, generateLast12Months } from '@utils/utils';
import { TIMESHEET_CONSTANTS } from '@pages/vendor-rep/timesheet/timesheet.constants';
import { TimesheetService } from '@app/pages/vendor-management/timesheet/timesheet.service';
import { EmployeeSkills } from '@app/shared/interfaces/employee-skiils';
import { MonthYearService } from '@services/monthyear.service';


@Component({
  selector: 'app-vendor-management-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    EmeraldModalModule,
    EmeraldDropdownModule,
    EmeraldButtonModule,
  ],
  templateUrl: './vendor-management-filter.component.html',
  styleUrl: './vendor-management-filter.component.scss',
})
export class VendorManagementFilterComponent implements OnInit, OnChanges {
  @Input() fieldsVisibility: string = '';
  @Input() contractorDataByOrgView: Contractors[] = [];
  @Input() ContractorDataBySkills: EmployeeSkills[] = [];
  @Input() contractorTimesheet: TimeSheetData[] = [];
  @Input() selectedEmployeeId: string = '';
  @Input() selectedMonth?: number;
  @Input() selectedYear?: number;
  @Input() resetTrigger: number = 0;
  @Output() filterApplied: EventEmitter<TimeSheetData[]> = new EventEmitter<
    TimeSheetData[]
  >();
  @Output() filterAppliedOrg = new EventEmitter<Contractors[]>();
  @Output() filterAppliedSkills = new EventEmitter<{
    data: EmployeeSkills[];
    buttonClick: string;
  }>();
  @Output() filterAppliedTimesheet = new EventEmitter<{
    data: any;
    buttonClick: string;
    month?: number;
    year?: number;
    vendor?: string;
    status?: string;
    location?: string;
    dntLeader?: string;
    employeeStatus?: string;
  }>();
  @Output() closeMenu = new EventEmitter<void>();

  opened: boolean = false;
  filterForm!: FormGroup;
  personaType = PersonaType;
  skillType = SkillType;
  openForm: boolean = true;
  hasAppliedOnce: boolean = false;
  primarySkillOptions: dropdownValues[] = [];
  secondarySkillOptions: dropdownValues[] = [];
  vendorOptions: dropdownValues[] = [];
  projectOptions: dropdownValues[] = [];
  monthOptions: dropdownValues[] = [];
  timesheetStatusOptions: dropdownValues[] = [
    { label: 'All', value: 'All' },
    { label: 'Pending Submission', value: 'Pending Submission' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Rejected', value: 'Rejected' },
    { label: 'Pending Approval', value: 'Pending Approval' },
  ];
  locationOptions: dropdownValues[] = [];
  dntLeaderOptions: dropdownValues[] = [];
  employeeStatusOptions: dropdownValues[] = [];
  displayData: (Contractors | EmployeeSkills | TimeSheetData)[] = [];

  constructor(
    private dataService: DataService,
    private toaster: ToasterService,
    private snackBar: MatSnackBar,
    private timesheetService: TimesheetService,
    private monthYearService: MonthYearService,
  ) {
    this.initializeMonthOptions();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadFormData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetTrigger'] && !changes['resetTrigger'].isFirstChange()) {
      this.reset(true);
    }

    if (changes['contractorTimesheet']?.currentValue) {
      this.updateLocationOptions();
      this.updateDntLeaderOptions();
      this.updateEmployeeStatusOptions();

      // When new team data arrives and a filter was already applied, re-apply it automatically
      if (!changes['contractorTimesheet'].isFirstChange() && this.hasAppliedOnce) {
        this.apply(true);
      }
    }

    if (
      changes['ContractorDataBySkills']?.currentValue ||
      changes['contractorDataByOrgView']?.currentValue
    ) {
      this.loadFormData();
    }
  }

  private initializeMonthOptions(): void {
    try {
      this.monthOptions = generateLast12Months();
    } catch (error) {
      this.openSnack('Error generating months');
      this.monthOptions = this.generateFallbackMonths();
    }
  }

  private generateFallbackMonths(): dropdownValues[] {
    const months: dropdownValues[] = [];
    const now = new Date();

    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthYear = date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });

      months.push({
        label: monthYear,
        value: `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`,
      });
    }

    return months;
  }

  private updateLocationOptions(): void {
    if (!this.contractorTimesheet?.length) {
      this.locationOptions = [{ label: 'All', value: 'All' }];
      return;
    }

    const uniqueCities = Array.from(
      new Set(
        this.contractorTimesheet
          .filter((item) => item?.location?.trim())
          .map((item) => item.location.trim()),
      ),
    );

    const locationOptions = sortDropdownValues(
      uniqueCities.map((city) => ({
        label: city,
        value: city,
      })),
    );
    
    this.locationOptions = [
      { label: 'All', value: 'All' },
      ...locationOptions
    ];
  }

  private updateDntLeaderOptions(): void {
    if (!this.contractorTimesheet?.length) {
      this.dntLeaderOptions = [{ label: 'All', value: 'All' }];
      return;
    }

    const uniqueLeaders = Array.from(
      new Set(
        this.contractorTimesheet
          .filter((item) => item?.l3ManagerName?.trim())
          .map((item) => item.l3ManagerName!.trim()),
      ),
    );

    const leaderOptions = sortDropdownValues(
      uniqueLeaders.map((leader) => ({
        label: leader,
        value: leader,
      })),
    );
    
    this.dntLeaderOptions = [
      { label: 'All', value: 'All' },
      ...leaderOptions
    ];
  }

  private updateEmployeeStatusOptions(): void {
    if (!this.contractorTimesheet?.length) {
      this.employeeStatusOptions = [{ label: 'All', value: 'All' }];
      return;
    }

    const uniqueStatuses = Array.from(
      new Set(
        this.contractorTimesheet
          .filter((item) => item?.employeeStatus?.trim())
          .map((item) => item.employeeStatus!.trim()),
      ),
    );

    const normalizedStatuses = new Set<string>();
    uniqueStatuses.forEach((status: string) => {
      if (status.toLowerCase() === 'Inactive') {
        normalizedStatuses.add('Inactive');
      } else if (status.toLowerCase() === 'active') {
        normalizedStatuses.add('Active');
      } else {
        normalizedStatuses.add(status);
      }
    });

    const statusOptions = sortDropdownValues(
      Array.from(normalizedStatuses).map((status) => ({
        label: status,
        value: status,
      })),
    );
    
    this.employeeStatusOptions = [
      { label: 'All', value: 'All' },
      ...statusOptions
    ];
  }

  /** Native <select> handler for the Timesheet filters — stores the full
   *  option object in the form, matching what the emerald dropdowns emitted. */
  onNativeSelect(controlName: string, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const optionsByControl: Record<string, dropdownValues[]> = {
      vendor: this.vendorOptions,
      dntLeader: this.dntLeaderOptions,
      status: this.timesheetStatusOptions,
      employeeStatus: this.employeeStatusOptions,
      location: this.locationOptions,
    };
    const option =
      optionsByControl[controlName]?.find((o) => String(o.value) === value) ??
      null;
    this.filterForm.get(controlName)?.setValue(option);
  }

  initializeForm(): void {
    this.filterForm = new FormGroup({
      primarySkill: new FormControl([]),
      secondarySkill: new FormControl([]),
      vendor: new FormControl(null),
      project: new FormControl(null),
      month: new FormControl(null),
      status: new FormControl(null),
      location: new FormControl(null),
      dntLeader: new FormControl(null),
      employeeStatus: new FormControl(null),
    });
  }

  loadFormData(): void {
    this.extractSkillOptions();
    this.loadVendorAndProjectOptions();
  }

  private extractSkillOptions(): void {
    const allSkillsSet = new Set<string>();

    this.contractorDataByOrgView?.forEach((contractor) => {
      this.addSkillsToSet(contractor, allSkillsSet);
    });

    this.ContractorDataBySkills?.forEach((contractor) => {
      this.addSkillsToSet(contractor, allSkillsSet);
    });

    const skillOptions = sortDropdownValues(
      Array.from(allSkillsSet)
        .filter((skill) => skill?.trim())
        .map((skill) => ({
          label: skill,
          value: skill,
        })),
    );

    this.primarySkillOptions = skillOptions.map((object) => ({ ...object }));
    this.secondarySkillOptions = skillOptions.map((object) => ({ ...object }));
  }

  private addSkillsToSet(
    contractor: Contractors | EmployeeSkills,
    skillsSet: Set<string>,
  ): void {
    const primarySkills = [];
    if (
      'primarySkill' in contractor &&
      typeof contractor.primarySkill === 'string' &&
      contractor?.primarySkill.trim()
    ) {
      primarySkills.push(contractor.primarySkill.trim());
    }
    if (
      'primarySkills' in contractor &&
      typeof contractor.primarySkills === 'string' &&
      contractor.primarySkills.trim()
    ) {
      primarySkills.push(
        ...contractor.primarySkills
          .split(',')
          .map((skill) => skill.trim())
          .filter(Boolean),
      );
    }
    primarySkills.forEach((skill) => skillsSet.add(skill));

    if (
      'secondarySkills' in contractor &&
      typeof contractor.secondarySkills === 'string' &&
      contractor?.secondarySkills.trim()
    ) {
      contractor.secondarySkills
        .split(',')
        .map((skill) => skill.trim())
        .filter(Boolean)
        .forEach((skill) => skillsSet.add(skill));
    }
  }

  private loadVendorAndProjectOptions(): void {
    try {
      const vendors = this.dataService.vendors();
      const vendorOptions = vendors ? getVendorOptions(vendors) : [];
      this.vendorOptions = [
        { label: 'All', value: 'All' },
        ...vendorOptions
      ];
    } catch (error) {
      this.openSnack('Error loading vendor options');
      this.vendorOptions = [{ label: 'All', value: 'All' }];
    }

    try {
      const projects = this.dataService.projects();
      if (projects) {
        const projectOptions = getProjectOptions(projects);
        const mappedProjectOptions =
          projectOptions?.map((option) => ({
            ...option,
            value: option.value?.toString() || option.label || '',
          })) || [];
        this.projectOptions = [
          { label: 'All', value: 'All' },
          ...mappedProjectOptions
        ];
      } else {
        this.projectOptions = [{ label: 'All', value: 'All' }];
      }
    } catch (error) {
      this.openSnack('Error loading project options');
      this.projectOptions = [{ label: 'All', value: 'All' }];
    }
  }

  private filterContractorsBySkill(
    contractors: (Contractors | EmployeeSkills)[],
    skillsToFilterBy: any[],
    skillKey: 'primarySkill' | 'secondarySkills' | 'primarySkills',
  ): (Contractors | EmployeeSkills)[] {
    if (!contractors?.length || !skillsToFilterBy?.length) {
      return contractors || [];
    }

    const selectedSkills = this.extractSelectedValues(skillsToFilterBy);
    if (!selectedSkills.length) {
      return contractors;
    }

    const lowerCaseSkills = selectedSkills.map((s) => s.toLowerCase());

    return contractors.filter((contractor) => {
      return this.contractorHasSkill(contractor, skillKey, lowerCaseSkills);
    });
  }

  private extractSelectedValues(items: any[]): string[] {
    return items
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.value) return item.value;
        if (item?.label) return item.label;
        return null;
      })
      .filter(Boolean);
  }

  private contractorHasSkill(
    contractor: Contractors | EmployeeSkills,
    skillKey: 'primarySkill' | 'secondarySkills' | 'primarySkills',
    targetSkills: string[],
  ): boolean {
    if (!contractor || !(skillKey in contractor)) return false;

    const contractorSkillValue = (contractor as any)[skillKey];
    if (!contractorSkillValue) return false;

    try {
      const contractorSkills = this.parseSkillValue(contractorSkillValue);
      const hasMatch = targetSkills.some((targetSkill) =>
        contractorSkills.includes(targetSkill),
      );

      return hasMatch;
    } catch (error) {
      this.openSnack('Error processing skills for contractor');
      return false;
    }
  }

  private parseSkillValue(skillValue: any): string[] {
    if (typeof skillValue === 'string') {
      return skillValue
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }

    if (Array.isArray(skillValue)) {
      return skillValue.map((s) => s?.trim()?.toLowerCase()).filter(Boolean);
    }

    this.openSnack('Unexpected skill value type');
    return [];
  }

  apply(isAutoReapply: boolean = false): void {
    try {
      const formValues = this.filterForm.value;

      const {
        primarySkill,
        secondarySkill,
        vendor,
        project,
        month,
        status,
        location,
        dntLeader,
        employeeStatus,
      } = formValues;

      const viewType = this.determineViewType();
      let filtered = this.getInitialDataset(viewType);

      if (viewType === 'timesheet') {
        this.hasAppliedOnce = true;
        filtered = this.applyTimesheetFilters(filtered as TimeSheetData[], {
          month,
          status,
          location,
          vendor,
          dntLeader,
          employeeStatus,
        }, isAutoReapply);
        if (month && month.value) {
          const monthYearValue = month.value;
          const [monthStr, yearStr] = monthYearValue.split('-') || [];
          const selectedMonth = Number(monthStr);
          const selectedYear = Number(yearStr);
          this.monthYearService.setMonthAndYear(selectedMonth, selectedYear);
        }
      } else {
        filtered = this.applyContractorFilters(
          filtered as (Contractors | EmployeeSkills)[],
          {
            primarySkill,
            secondarySkill,
            vendor,
            project,
            isSkillsView: viewType === 'skills',
          },
        );
      }

      this.emitFilterResults(viewType, filtered);
     if (!isAutoReapply) {
        this.closeMenu.emit();
      }
    } catch (error) {
      this.openSnack('Error applying filters');
      this.showError('Error applying filters. Please try again.');
    }
  }

  private determineViewType(): 'timesheet' | 'skills' | 'org' {
    if (this.fieldsVisibility === PersonaType.Timesheet) return 'timesheet';
    if (this.fieldsVisibility === PersonaType.SkillsView || this.ContractorDataBySkills?.length > 0) {
      return 'skills';
    }
    return 'org';
  }

  private getInitialDataset(viewType: string): any[] {
    switch (viewType) {
      case 'timesheet':
        return [...(this.contractorTimesheet || [])];
      case 'skills':
        return [...(this.ContractorDataBySkills || [])];
      default:
        return [...(this.contractorDataByOrgView || [])];
    }
  }

  private emitFilterResults(viewType: string, filtered: any[]): void {
    switch (viewType) {
      case 'timesheet':
        const formValues = this.filterForm.value;
        let selectedMonth: number | undefined;
        let selectedYear: number | undefined;
        if (formValues.month) {
          selectedMonth = formValues.month.value;
          selectedYear = Number(formValues.month.label.split(' ')[1]);
        }
      
        this.filterAppliedTimesheet.emit({
          data: filtered as TimeSheetData[],
          buttonClick: 'apply',
          month: selectedMonth,
          year: selectedYear,
          vendor: formValues.vendor?.value !== 'All' ? formValues.vendor?.value : undefined,
          status: formValues.status?.value !== 'All' ? formValues.status?.value : undefined,
          location: formValues.location?.value !== 'All' ? formValues.location?.value : undefined,
          dntLeader: formValues.dntLeader?.value !== 'All' ? formValues.dntLeader?.value : undefined,
          employeeStatus: formValues.employeeStatus?.value !== 'All' ? formValues.employeeStatus?.value : undefined,
        });
        break;
      case 'skills':
        this.filterAppliedSkills.emit({
          data: filtered as EmployeeSkills[],
          buttonClick: 'apply',
        });
        break;
      default:
        this.filterAppliedOrg.emit(filtered as Contractors[]);
        break;
    }
  }

  private applyTimesheetFilters(
    data: TimeSheetData[],
    filters: { month: any; status: any; location: any; vendor: any; dntLeader: any; employeeStatus: any },
    isAutoReapply: boolean = false,
  ): TimeSheetData[] {
    let filtered = data;

    if (!isAutoReapply) {
      this.MonthSelection(
        filters.vendor?.value === 'All' ? undefined : filters.vendor?.value,
        filters.location?.value === 'All' ? undefined : filters.location?.value,
        filters.status?.value === 'All' ? 'All' : filters.status?.value,
        filters.dntLeader?.value === 'All' ? undefined : filters.dntLeader?.value,
        filters.employeeStatus?.value === 'All' ? undefined : filters.employeeStatus?.value,
      );
    }

    if (filters.status?.value && filters.status.value !== 'All') {
      filtered = filtered?.filter((item) => {
        let statusValue = item?.status;
        if (statusValue == null) {
          statusValue =
            item?.workingHrs === 0 && item?.billableHrs === 0
              ? SkillType.Submission
              : SkillType.Pending;
        }
        return (
          statusValue?.toLowerCase() === filters.status.value.toLowerCase()
        );
      });
    }

    // Filter by city/location
    if (filters.location?.value && filters.location.value !== 'All') {
      filtered = filtered.filter(
        (item) =>
          (item.location || '').toLowerCase() ===
          filters.location.value.toLowerCase(),
      );
    }

    if ((filters.vendor?.label || filters.vendor?.value) && filters.vendor?.value !== 'All') {
      const vendorName = filters.vendor.label || filters.vendor.value;
      filtered = filtered.filter(
        (item) =>
          (item.vendorName || '').toLowerCase() === vendorName.toLowerCase(),
      );
    }

    if (filters.dntLeader?.value && filters.dntLeader.value !== 'All') {
      filtered = filtered.filter(
        (item) =>
          (item.l3ManagerName || '').toLowerCase() === filters.dntLeader.value.toLowerCase(),
      );
    }

    if (filters.employeeStatus?.value && filters.employeeStatus.value !== 'All') {
      filtered = filtered.filter((item) => {
        if (filters.employeeStatus.value.toLowerCase() === 'Inactive') {
          return item.employeeStatus?.toLowerCase() === 'Inactive';
        }
        return item.employeeStatus === filters.employeeStatus.value;
      });
    }

    return filtered;
  }

  private extractMonthFromDate(item: TimeSheetData): string | null {
    try {
      const date = new Date((item as any).date || (item as any).submissionDate);
      if (isNaN(date.getTime())) return null;

      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    } catch {
      return null;
    }
  }

  private applyContractorFilters(
    data: (Contractors | EmployeeSkills)[],
    filters: {
      primarySkill: any;
      secondarySkill: any;
      vendor: any;
      project: any;
      isSkillsView: boolean;
    },
  ): (Contractors | EmployeeSkills)[] {
    let filtered = data;

    if (filters.primarySkill?.length > 0) {
      const skillKey =
        this.fieldsVisibility === PersonaType.SkillsView
          ? 'primarySkills'
          : 'primarySkill';
      filtered = this.filterContractorsBySkill(
        filtered,
        filters.primarySkill,
        skillKey,
      );
    }

    if (filters.secondarySkill?.length > 0) {
      filtered = this.filterContractorsBySkill(
        filtered,
        filters.secondarySkill,
        'secondarySkills',
      );
    }

    if ((filters.vendor?.label || filters.vendor?.value) && filters.vendor?.value !== 'All') {
      const vendorName = filters.vendor.label || filters.vendor.value;
      filtered = filtered.filter(
        (contractor) =>
          (contractor?.vendorName || '').toLowerCase() ===
          vendorName.toLowerCase(),
      );
    }

    if (filters.project?.label && filters.project.value !== 'All' && filters.isSkillsView) {
      filtered = filtered.filter(
        (contractor) =>
          'projectName' in contractor &&
          (contractor as EmployeeSkills).projectName?.toLowerCase() ===
            filters.project.label.toLowerCase(),
      );
    }

    return filtered;
  }

  reset(silent: boolean = false): void {
    try {
      this.hasAppliedOnce = false;
      this.filterForm.reset();
      this.filterForm.markAsPristine();
      this.filterForm.markAsUntouched();
      this.openForm = false;
      setTimeout(() => {
        this.openForm = true;
      });

      if (silent) return;

      if (this.fieldsVisibility === PersonaType.Timesheet) {
        this.filterAppliedTimesheet.emit({
          data: this.contractorTimesheet,
          buttonClick: 'reset',
        });
        return;
      } else if (this.fieldsVisibility === PersonaType.SkillsView) {
        this.filterAppliedSkills.emit({
          data: this.ContractorDataBySkills,
          buttonClick: 'reset',
        });
      } else {
        this.filterAppliedOrg.emit(this.contractorDataByOrgView);
      }
    } catch (error) {
      this.openSnack('Error resetting filters');
      this.showError('Error resetting filters. Please try again.');
    }
  }

  private showError(message: string): void {
    this.toaster.showError(message, 'Error');
  }

  private openSnack(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  MonthSelection(vendor?: string, location?: string, status?: string, dntLeader?: string, employeeStatus?: string) {
    const Id = JSON.parse(sessionStorage.getItem('userProfile') as string);
    const resolvedEmployeeId = this.selectedEmployeeId || Id.employeeId;
    const filteredData = this.filterForm.get('month')?.value;
    const monthYearValue = filteredData?.value;

    let filteredMonth: number;
    let filteredYear: number;

    if (this.fieldsVisibility === PersonaType.Timesheet && this.selectedMonth !== undefined && this.selectedYear !== undefined) {
      filteredMonth = this.selectedMonth;
      filteredYear = this.selectedYear;
    } else if (monthYearValue) {
      const [monthStr, yearStr] = monthYearValue.split('-') || [];
      filteredMonth = Number(monthStr);
      filteredYear = Number(yearStr);
    } else {
      const now = new Date();
      filteredMonth = now.getMonth() + 1;
      filteredYear = now.getFullYear();
    }
    this.loadTimesheetData(
      resolvedEmployeeId,
      filteredMonth,
      filteredYear,
      vendor,
      status,
      location,
      dntLeader,
      employeeStatus,
    );
  }

  private loadTimesheetData(
    employeeId: string,
    month: number,
    year: number,
    vendor?: string,
    status?: string,
    location?: string,
    dntLeader?: string,
    employeeStatus?: string,
  ) {
    this.timesheetService
      .getContractorsTimesheet(
        employeeId,
        month,
        year,
        {
        vendor,
        status,
        location,
        dntLeader,
        employeeStatus,
      }
      )
      .subscribe({
        next: (response: TimesheetResponse) => {
          let responseData = response?.data || [];
          this.filterAppliedTimesheet.emit({
            data: responseData,
            buttonClick: '',
          });
        },
        error: () => {
          this.toaster.showError(
            TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_TIMESHEET,
            TIMESHEET_CONSTANTS.TITLES.ERROR,
          );
        },
      });
  }
}
